import { useCallback, useState } from 'react';

import { AdvancedMenuItemComponentProps } from '../../../../../types/menus';
import { setNodeAttr } from '../../../../../utils';

const INITIAL_SECTION = [
  {
    time: 0,
    label: '',
  },
  {
    time: 10,
    label: '',
  },
];

type SectionType = { label: string; time: number };

export const SECONDS_IN_HOUR = 3600;
export const SECONDS_IN_MINUTES = 60;

export const useTimeStamps = (props: AdvancedMenuItemComponentProps) => {
  const { view, currentSelectionNode, pos } = props;
  const [sections, setSections] = useState<SectionType[]>(
    currentSelectionNode.attrs.sections ?? [],
  );
  const showHourInput = currentSelectionNode.attrs.duration >= SECONDS_IN_HOUR;

  const updateSections = useCallback(
    (newSections: SectionType[]) => {
      setSections(newSections);
      setNodeAttr(view, pos, 'sections', newSections);
    },
    [pos, view],
  );

  const onAddSection = useCallback(() => {
    const newSections = !sections.length
      ? INITIAL_SECTION
      : [
          ...sections,
          {
            time: Math.min(
              sections[sections.length - 1].time + 10,
              currentSelectionNode.attrs.duration,
            ),
            label: '',
          },
        ];

    updateSections(newSections);
  }, [currentSelectionNode.attrs.duration, sections, updateSections]);

  const onDeleteSection = useCallback(
    (deleteIndex: number) => {
      const newSections =
        sections.length <= 2 ? [] : sections.filter((_item, index) => index !== deleteIndex);

      updateSections(newSections);
    },
    [sections, updateSections],
  );

  const onLabelChange = useCallback(
    (index: number, newLabel: string) => {
      const newSections = [...sections];
      newSections[index] = {
        ...newSections[index],
        label: newLabel,
      };

      updateSections(newSections);
    },
    [sections, updateSections],
  );

  const onTimeChange = useCallback(
    (index: number, time: number) => {
      if (!time || time > currentSelectionNode.attrs.duration) {
        return;
      }
      const newSections = [...sections];
      newSections[index] = {
        ...newSections[index],
        time,
      };

      updateSections(newSections);
    },
    [currentSelectionNode.attrs.duration, sections, updateSections],
  );

  const onTimeChangeSort = useCallback(() => {
    const newSections = [...sections];

    newSections.sort((firstSection, secondSection) => firstSection.time - secondSection.time);

    updateSections(newSections);
  }, [sections, updateSections]);

  return {
    sections,
    onAddSection,
    onDeleteSection,
    onLabelChange,
    onTimeChange,
    onTimeChangeSort,
    showHourInput,
  };
};
